import { WithSkeleton } from '@capsicum/common';
import { ListCard, Reversible, SkeletonItem } from '@capsicum/common-web';
import { ChevronRightIcon } from '@capsicum/common-web/icons';
import { type WalletFanToken } from '@capsicum/features/wallet/shared';
import { ShortCode } from '@capsicum/socios-core';
import styled, { useTheme } from 'styled-components';

export const WalletFanTokenListCardSkeleton = ({
	mainComponentProps: { onBodyClick }
}: {
	mainComponentProps: WalletFanTokenListCardProps;
}) => {
	const { colors } = useTheme();

	const clickable = !!onBodyClick;

	return (
		<S.ListCardWrapper>
			<ListCard.Start>
				<ListCard.Image isLoading src="" />
			</ListCard.Start>
			<S.ContentWrapper $clickable={clickable}>
				<ListCard.Content>
					<S.ListCardRow>
						<S.ListCardTitle>
							<SkeletonItem height={20} width={128} />
						</S.ListCardTitle>
						<S.ListCardText>
							<SkeletonItem height={20} width={64} />
						</S.ListCardText>
					</S.ListCardRow>
					<ListCard.ContentDivider />
					<S.ListCardRow>
						<S.ListCardTitle>
							<SkeletonItem width={128} />
						</S.ListCardTitle>
						<S.ListCardText>
							<SkeletonItem height={14} width={128} />
						</S.ListCardText>
					</S.ListCardRow>
				</ListCard.Content>
				{clickable && (
					<S.ListCardEndTop>
						<ChevronRightIcon color={colors.transparent} height={20} width={20} />
					</S.ListCardEndTop>
				)}
			</S.ContentWrapper>
		</S.ListCardWrapper>
	);
};

export type WalletFanTokenListCardProps = {
	isLoadingToken?: boolean;
	item?: WalletFanToken;
	onBodyClick?: () => void;
	onIconClick?: () => void;
	subtitle?: string;
	testID?: string;
};

export const WalletFanTokenListCardComponent = ({
	item,
	onBodyClick,
	onIconClick,
	subtitle,
	testID
}: WalletFanTokenListCardProps) => {
	const { altCurrency, defaultCurrency } = item?.walletConvertedPrices ?? {};
	const { currency } = item?.ledger ?? {};
	const isLoyaltyToken = ShortCode.SSU === currency?.shortCode;
	const { colors } = useTheme();
	const hasConvertedBalance =
		defaultCurrency?.formattedTotalBalance &&
		item?.totalBalance !== 0 &&
		(item?.ledger?.convertedPrices as [])?.length !== 0;

	return (
		<S.ListCardWrapper testID={testID ? testID : `wallet-fanToken-listCard-${item?.partner?.id}`}>
			<ListCard.Start>
				<div>
					<ListCard.Image
						blurhash={item?.partner?.blurHash}
						src={item?.partner?.logo}
						testID={`wallet-fanToken-partnerLogo-${item?.partner.id}`}
						onClick={onIconClick}
					/>
				</div>
			</ListCard.Start>
			<S.ContentWrapper $clickable={onBodyClick ? true : false} onClick={onBodyClick}>
				<ListCard.Content>
					<S.ListCardRow>
						<S.ListCardTitle data-testid={`wallet-fanTokens-cardTeamName-${item?.partner?.name}`}>
							{item?.partner?.name}
						</S.ListCardTitle>
						<S.ListCardText testID={`wallet-fanTokens-cardPartnerBalance-${item?.partner?.name}`}>
							{!isLoyaltyToken && (item?.totalBalance || '0') + ' ' + currency?.shortCode}
						</S.ListCardText>
					</S.ListCardRow>
					<ListCard.ContentDivider />
					<S.ListCardRowRight>
						{Boolean(subtitle) && <S.ListCardSubTitle>{subtitle}</S.ListCardSubTitle>}
						<ListCard.SubTitle>
							{!isLoyaltyToken ? (
								hasConvertedBalance ? (
									<div>
										<S.DefaultCurrencyContainer>{`${defaultCurrency?.formattedTotalBalance} `}</S.DefaultCurrencyContainer>
										{altCurrency?.balance && (
											<S.AltCurrencyContainer>{`≈${altCurrency?.formattedTotalBalance}`}</S.AltCurrencyContainer>
										)}
									</div>
								) : (
									'-- --'
								)
							) : (
								<S.DefaultCurrencyContainer>{`${item?.ledger?.balance} ${currency?.shortCode}`}</S.DefaultCurrencyContainer>
							)}
						</ListCard.SubTitle>
					</S.ListCardRowRight>
				</ListCard.Content>
				{onBodyClick && (
					<S.ListCardEndTop>
						<Reversible>
							<ChevronRightIcon color={colors.text.main.enabled} height={20} width={20} />
						</Reversible>
					</S.ListCardEndTop>
				)}
			</S.ContentWrapper>
		</S.ListCardWrapper>
	);
};

export const WalletFanTokenListCard = WithSkeleton(WalletFanTokenListCardComponent, WalletFanTokenListCardSkeleton);

namespace S {
	export const ListCardWrapper = styled(ListCard)`
		width: 100%;
	`;
	export const ListCardRow = styled(ListCard.Row)`
		justify-content: center;
	`;
	export const ListCardTitle = styled(ListCard.Title)`
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
		margin-right: auto;
		max-width: 250px;
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			max-width: 115px;
		}
	`;
	export const ListCardSubTitle = styled(ListCard.SubTitle)`
		flex: 1;
	`;
	export const ListCardText = styled(ListCard.Text)`
		${({ theme }) => theme.fonts.text.text1}
		white-space: nowrap;
	`;
	export const ListCardEndTop = styled(ListCard.End)`
		align-items: flex-start;
		margin-left: 0;
	`;
	export const ListCardRowRight = styled(ListCard.Row)`
		justify-content: flex-end;
	`;
	export const DefaultCurrencyContainer = styled.span`
		${({ theme }) => theme.fonts.text.caption1}
	`;
	export const AltCurrencyContainer = styled.span`
		padding-left: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.text.caption2}
	`;
	export const ContentWrapper = styled.div<{
		$clickable: boolean;
	}>`
		width: 100%;
		display: flex;
		flex-direction: row;
		order: 2;
		${({ $clickable }) => $clickable && `cursor: pointer`}
		${({ $clickable, theme }) => !$clickable && `padding-right: ${theme.spacings.medium}`}
	`;
}
